import type { ReportComponent } from '@/types/reports';
import {
  getFilledBrands,
  getFilledCreators,
  getFilledHashtagCategories,
} from '@/pages/reports/components/ui/TiktokTrends/utils/hasContent';

const MAX_LENGTH = 60;

const truncate = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.length <= MAX_LENGTH) return trimmed;
  return `${trimmed.slice(0, MAX_LENGTH - 1)}…`;
};

const firstFilled = (...values: Array<string | undefined | null>): string => {
  for (const value of values) {
    const text = (value ?? '').toString().trim();
    if (text) return text;
  }
  return '';
};

const formatBadgeLabel = (props: Record<string, any>): string => {
  const badge = (props.badgeLabel ?? '').toString().trim();
  if (!badge) return '';
  if (badge === 'Outro') {
    const custom = (props.badgeLabelCustom ?? '').toString().trim();
    return custom || 'Outro';
  }
  return badge;
};

export function getComponentSummary(component: ReportComponent): string {
  const props = component.props ?? {};

  switch (component.type) {
    case 'hero': {
      const edition = (props.edition ?? '').toString().trim();
      return edition ? `Edição ${edition}` : '';
    }
    case 'news-card': {
      const badge = formatBadgeLabel(props);
      const title = (props.title ?? '').toString().trim();
      if (badge && title) return truncate(`${badge} · ${title}`);
      return truncate(badge || title);
    }
    case 'business-card': {
      return truncate(firstFilled(props.sectionTitle, props.title));
    }
    case 'highlights-card': {
      const items = Array.isArray(props.items) ? props.items : [];
      return items.length > 0 ? `${items.length} item(ns)` : '';
    }
    case 'today-carousel': {
      const slides = Array.isArray(props.slides) ? props.slides : [];
      return slides.length > 0 ? `${slides.length} slide(s)` : '';
    }
    case 'thematic-session': {
      return truncate((props.title ?? '').toString());
    }
    case 'curatorship': {
      const items = Array.isArray(props.items) ? props.items : [];
      const filled = items.filter((item: any) =>
        (item?.title ?? '').toString().trim() && (item?.url ?? '').toString().trim()
      );
      return filled.length > 0 ? `${filled.length} link(s)` : '';
    }
    case 'tiktok-trends': {
      const counts = [
        getFilledHashtagCategories(props.hashtagCategories).length,
        getFilledCreators(props.creators).length,
        getFilledBrands(props.brands).length,
      ];
      const total = counts.reduce((sum, value) => sum + value, 0);
      return total > 0 ? `${total} entrada(s)` : '';
    }
    default: {
      return truncate(firstFilled(props.title, props.sectionTitle));
    }
  }
}
